import React, { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { colors, useTheme } from '@iq/react-components';
import { scaleOrdinal, scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Grid } from '@visx/grid';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';

import {
  usePanelDateDomain,
  useSelectedDateDomain,
  useActiveTimelineData,
  useOnSelectedDateDomainChange,
  useStyling,
  useZScale,
  useSelectedEventType,
} from './EventTimelineState';
import { IotBucketTooltip, IotEventTooltip } from './EventTooltips';
import { getTimezone } from '../../../bundles/application';
import { getStaticComponents } from '../../../bundles/components';
import { useClientSize } from '../../../utils';
import { getDateTickFormatter } from '../../../datetimeUtils';
import EventLegends from './EventLegends';
import { EVENT_DEFAULT_MARGIN, EVENT_TYPE_DTMP, EVENT_TYPE_MACH } from '../../../constants';
import {
  formatYTick,
  useEventPicker,
  useTimelineDragZoom,
  useTimeScale,
  useTimelineTooltipPortal,
  getTimelineConstants,
  defaultTooltipStyles,
  getComponentNameForDtmpEvent,
} from './utils';

// Min px between the min and max bars for both labels to fit without overlapping
const MIN_LABEL_SPACING = 100;

function renderIotEventTooltip(event, timezone) {
  return {
    eventId: event.id,
    tooltip: (
      <IotEventTooltip
        event={event}
        timezone={timezone}
      />
    ),
  };
}

function renderIotBucketTooltip(bucket, timezone) {
  return {
    tooltip: (
      <IotBucketTooltip
        bucket={bucket}
        timezone={timezone}
      />
    ),
  };
}

const IotTimeline = ({ margin = EVENT_DEFAULT_MARGIN, plotSize = 4, onOpenEvent, fullWidth }) => {
  const timezone = useSelector(getTimezone);
  const staticComponents = useSelector(getStaticComponents);
  const theme = useTheme();
  const plotSize2 = plotSize * 2;

  const zScale = useZScale();

  const [{ width }, clientReference] = useClientSize();

  const { tickStroke, background, indicatorStroke, xTickLabelProps, yTickLabelProps } =
    useStyling();

  const selectedDateDomain = useSelectedDateDomain();
  const panelDateDomain = usePanelDateDomain();

  // Since this can be a bit confusing, we have different types of events: User Events and IoT Events.
  // User Events are the events created by users (EVENT_TYPE_USER) and IoT Events are the events
  // coming from IoT devices (EVENT_TYPE_MACH and EVENT_TYPE_DTMP).
  // The selectedEventType is one of EVENT_TYPE_USER, EVENT_TYPE_MACH, or EVENT_TYPE_DTMP.
  // Then those events themselves have internal types like: Normal, Warning, Emergency, etc.

  const [selectedEventType] = useSelectedEventType();
  const onZoomChange = useOnSelectedDateDomainChange();

  let periodInMs = panelDateDomain ? panelDateDomain[1] - panelDateDomain[0] : 0;

  if (selectedDateDomain) {
    periodInMs = selectedDateDomain[1] - selectedDateDomain[0];
  }

  const hitMaxZoom = periodInMs <= 2;

  const {
    response: {
      userEvents = [],
      userEventTypes = [],
      iotBuckets = [],
      dateDomain = selectedDateDomain?.length ? selectedDateDomain : panelDateDomain,
    } = {},
  } = useActiveTimelineData();

  const userEventsReference = useRef(userEvents);
  userEventsReference.current = userEvents;
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip();
  const { containerRef, TooltipInPortal } = useTimelineTooltipPortal();

  const onOpenIOTEvent = useCallback(
    (event) => {
      if (event && onOpenEvent) {
        const eventType = event.messageType ? EVENT_TYPE_DTMP : EVENT_TYPE_MACH;
        const componentName =
          eventType === EVENT_TYPE_DTMP
            ? getComponentNameForDtmpEvent(event, staticComponents)
            : '';

        onOpenEvent({ event, id: event.id, type: eventType, componentName });
      }
    },
    [onOpenEvent, staticComponents]
  );

  const { PADDING_BOTTOM, IOT_EVENT_HEIGHT } = getTimelineConstants();

  const height = IOT_EVENT_HEIGHT + margin.top + margin.bottom + PADDING_BOTTOM;

  const xSize = width - margin.right - margin.left;
  const xScale = useTimeScale(dateDomain, width, margin);
  const yScale = useMemo(() => {
    const withIot = iotBuckets.length > 0;
    const domain = withIot ? ['iot'] : ['default'];
    let range = [margin.top + IOT_EVENT_HEIGHT / 2];
    if (!withIot && userEventTypes.length === 0) {
      range = [margin.top + IOT_EVENT_HEIGHT / 2];
    }
    return scaleOrdinal({ domain, range });
  }, [iotBuckets.length, margin.top, IOT_EVENT_HEIGHT, userEventTypes.length]);

  const { maxNumEvents, minNumEvents } = useMemo(() => {
    const values = iotBuckets.filter((d) => d.numEvents > 1).map((d) => d.numEvents);
    return {
      maxNumEvents: Math.max(...values),
      minNumEvents: Math.min(...values),
    };
  }, [iotBuckets]);

  const BUCKET_LABEL_CLEARANCE = 12;

  const heightScale = useMemo(
    () =>
      scaleLinear({
        domain: [Math.max(0, minNumEvents), Math.max(100, maxNumEvents)],
        range: [4, IOT_EVENT_HEIGHT - BUCKET_LABEL_CLEARANCE],
      }),
    [minNumEvents, maxNumEvents, IOT_EVENT_HEIGHT]
  );

  const maxBucketIndex = useMemo(
    () => iotBuckets.findIndex((item) => item.numEvents === maxNumEvents),
    [iotBuckets, maxNumEvents]
  );

  const minBucketIndex = useMemo(
    () => iotBuckets.findIndex((item) => item.numEvents === minNumEvents),
    [iotBuckets, minNumEvents]
  );

  const maxBarHeight = plotSize2 + heightScale(maxNumEvents);

  const { zoomReference, onDragStart, onDrag, onDragEnd } = useTimelineDragZoom(
    xScale,
    hitMaxZoom,
    onZoomChange
  );

  const criticalColor = zScale('critical');
  const warningColor = zScale('warning');
  const infoColor = zScale('info');

  const iotGrads = useMemo(
    () => (
      <defs>
        {iotBuckets.map((bucket, bucketIndex) => {
          if (bucket.numEvents < 2) {
            return null;
          }

          const severityCounts = {
            critical: 0,
            warning: 0,
            minor: 0,
            info: 0,
            internal: 0,
          };
          for (const item of bucket.numEventsPerType) {
            severityCounts[item.severity] = Math.max(
              5,
              Math.ceil((item.count / bucket.numEvents) * 100)
            );
          }
          const { critical, warning } = severityCounts;
          return (
            <linearGradient
              key={bucket.from}
              id={`bucket-gradient-${bucketIndex}`}
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop
                offset="0%"
                style={{ stopColor: criticalColor, stopOpacity: 1 }}
              />
              <stop
                offset={`${critical}%`}
                style={{ stopColor: criticalColor, stopOpacity: 1 }}
              />
              <stop
                offset={`${critical}%`}
                style={{ stopColor: warningColor, stopOpacity: 1 }}
              />
              <stop
                offset={`${warning + critical}%`}
                style={{ stopColor: warningColor, stopOpacity: 1 }}
              />
              <stop
                offset={`${warning + critical}%`}
                style={{ stopColor: infoColor, stopOpacity: 1 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: infoColor, stopOpacity: 1 }}
              />
            </linearGradient>
          );
        })}
      </defs>
    ),
    [iotBuckets, criticalColor, warningColor, infoColor]
  );

  const [eventPicker, openEventPicker] = useEventPicker();
  const iotShapes = useMemo(
    () => (
      <>
        {iotBuckets.map((bucket, bucketIndex) => {
          if (bucket.numEvents === 1 && bucket.events?.[0]) {
            const event = bucket.events[0];
            const mouseOver = (mouseEvent) => {
              const coords = localPoint(mouseEvent.target.ownerSVGElement, mouseEvent);
              showTooltip({
                tooltipLeft: coords.x,
                tooltipTop: coords.y,
                tooltipData: renderIotEventTooltip(event, timezone),
              });
            };

            return (
              <rect
                className="timeline-chart-event"
                key={bucket.from}
                x={xScale(new Date(event.timestamp).getTime()) - plotSize}
                y={yScale('iot') - plotSize}
                rx={plotSize}
                ry={plotSize}
                width={plotSize2}
                height={plotSize2}
                fill={zScale(event.severity)}
                onMouseOver={mouseOver}
                onMouseOut={hideTooltip}
                onClick={() => onOpenIOTEvent(event)}
              />
            );
          }
          const onClick = (event) => {
            if (hitMaxZoom) {
              const { x, y, width: w, height: h } = event.target.getBBox();
              const { top, right } = event.target.getBoundingClientRect();
              const placeAbove = top > window.innerHeight / 2;
              const alignLeft = right < (window.innerWidth * 2) / 3;

              openEventPicker({
                events: bucket.events || [],
                onSelect: onOpenIOTEvent,
                position: {
                  placeAbove,
                  alignLeft,
                  top: placeAbove ? y : y + h,
                  left: alignLeft ? x : x + w,
                },
                timezone,
              });
            } else if (bucket.numEvents < 10 && !hitMaxZoom) {
              const timestamps = (bucket.events || [])
                .map((event) => new Date(event.timestamp).getTime())
                .filter((t) => !Number.isNaN(t));
              if (timestamps.length > 0) {
                const from = Math.min(...timestamps);
                const to = Math.max(...timestamps);
                const diff = Math.max(1, to - from);
                onZoomChange([from - diff, to + diff, null]);
              } else {
                onZoomChange([
                  new Date(bucket.from).getTime(),
                  new Date(bucket.to).getTime(),
                  null,
                ]);
              }
            } else if (bucket.numEvents >= 10) {
              onZoomChange([new Date(bucket.from).getTime(), new Date(bucket.to).getTime(), null]);
            }
            hideTooltip();
          };
          const mouseOver = (event) => {
            const coords = localPoint(event.target.ownerSVGElement, event);
            showTooltip({
              tooltipLeft: coords.x,
              tooltipTop: coords.y,
              tooltipData: renderIotBucketTooltip(bucket, timezone),
            });
          };

          const { numEvents } = bucket;
          const x1 = xScale(new Date(bucket.from).getTime()) + 1;
          const x2 = xScale(new Date(bucket.to).getTime()) - 1;
          const y = yScale('iot');
          const isHighest = bucketIndex === maxBucketIndex;
          const barHeight = plotSize2 + heightScale(numEvents);
          const numLabelY = y - maxBarHeight / 2 - 4;
          const x = x1 + (x2 - x1) / 2;
          const scaledStart = xScale(dateDomain[0]);
          const scaledEnd = xScale(dateDomain[1]);
          const maxBucket = iotBuckets[maxBucketIndex];
          const maxX =
            (xScale(new Date(maxBucket.from).getTime()) +
              xScale(new Date(maxBucket.to).getTime())) /
            2;
          const isMinLabelVisible = Math.abs(x - maxX) >= MIN_LABEL_SPACING;
          const showNumberLabel =
            x > scaledStart &&
            x < scaledEnd &&
            (isHighest || (bucketIndex === minBucketIndex && isMinLabelVisible));
          let textAnchor = 'middle';
          if (x < scaledEnd / 3) {
            textAnchor = 'start';
          }
          if (x > (scaledEnd / 3) * 2) {
            textAnchor = 'end';
          }

          return (
            <React.Fragment key={bucket.from}>
              {showNumberLabel && (
                <>
                  <text
                    x={x}
                    y={numLabelY - 5}
                    {...xTickLabelProps()}
                    textAnchor={textAnchor}
                  >
                    {numEvents} events
                  </text>
                  <line
                    x1={x}
                    x2={x}
                    y1={y}
                    y2={numLabelY - 2}
                    stroke={tickStroke}
                  />
                </>
              )}
              <rect
                className="timeline-chart-event"
                x={x1}
                y={y - barHeight / 2}
                rx={plotSize}
                ry={plotSize}
                width={Math.max(0, x2 - x1)}
                height={barHeight}
                fill={`url(#bucket-gradient-${bucketIndex})`}
                onClick={onClick}
                onMouseOver={mouseOver}
                onMouseOut={hideTooltip}
              />
            </React.Fragment>
          );
        })}
      </>
    ),
    [
      iotBuckets,
      xScale,
      yScale,
      maxBucketIndex,
      dateDomain,
      minBucketIndex,
      xTickLabelProps,
      tickStroke,
      plotSize2,
      heightScale,
      maxBarHeight,
      plotSize,
      hideTooltip,
      zScale,
      showTooltip,
      timezone,
      onOpenIOTEvent,
      hitMaxZoom,
      openEventPicker,
      onZoomChange,
    ]
  );

  const presentIndicator = useMemo(() => {
    const now = Date.now();

    if (dateDomain[1] - now <= 0) {
      return null;
    }

    const x = xScale(now) || 0;
    return (
      <line
        x1={x}
        y1={0}
        x2={x}
        y2={height - margin.bottom}
        stroke={indicatorStroke}
        strokeOpacity={0.5}
      />
    );
  }, [dateDomain, xScale, height, margin.bottom, indicatorStroke]);

  return (
    <>
      {[EVENT_TYPE_DTMP, EVENT_TYPE_MACH].includes(selectedEventType) && (
        <>
          <EventLegends
            fullWidth={fullWidth}
            eventType={selectedEventType}
          />
          <div
            ref={clientReference}
            className="event-timeline-chart-area"
          >
            <svg
              width={width}
              height={height}
              ref={containerRef}
              onTouchMove={onDrag}
              onTouchEnd={onDragEnd}
              onMouseUp={onDragEnd}
              onMouseLeave={onDragEnd}
              onMouseMove={onDrag}
            >
              {presentIndicator}
              {iotGrads}
              <rect
                ref={zoomReference}
                fillOpacity={0}
                stroke="currentColor"
                strokeDasharray="1,2"
                width={0}
                height={height - margin.top - margin.bottom - 10}
                y={margin.top - 5}
              />
              <rect
                fillOpacity={0}
                x={margin.left}
                stroke={tickStroke}
                width={Math.max(0, width - margin.left - margin.right - 2)}
                height={height - margin.bottom}
                onTouchStart={onDragStart}
                onMouseDown={onDragStart}
              />
              <Grid
                xScale={xScale}
                yScale={yScale}
                width={Math.max(0, width - margin.left - margin.right)}
                height={height}
                left={margin.left}
                columnLineStyle={{ display: 'none' }}
                stroke={tickStroke}
              />
              {iotShapes}

              <rect
                // covers any overflow from chart
                x={0}
                y={0}
                width={margin.left}
                height={height}
                fill={background}
              />
              <AxisLeft
                scale={yScale}
                left={margin.left}
                hideAxisLine
                stroke={tickStroke}
                tickStroke="transparent"
                tickLabelProps={yTickLabelProps}
                tickFormat={(value) => formatYTick(value)}
              />
              <AxisBottom
                scale={xScale}
                top={height - margin.bottom}
                hideAxisLine
                stroke={tickStroke}
                tickStroke={tickStroke}
                tickLabelProps={xTickLabelProps}
                numTicks={Math.max(Math.floor(xSize / 80), 1)}
                tickFormat={getDateTickFormatter(periodInMs, timezone, {
                  scale: xScale,
                  width: width - margin.right,
                })}
              />
              <rect
                // covers removed end tick line
                x={width - margin.right - 20}
                y={height - margin.bottom + 1}
                width={30}
                height={8}
                fill={background}
              />
            </svg>
            {tooltipOpen && (
              <TooltipInPortal
                key={`${tooltipTop}-${tooltipLeft}-${tooltipData?.eventId || 'bucket'}`}
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultTooltipStyles,
                  padding: 0,
                  borderRadius: '0.2rem',
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme === 'dark' ? colors.Grey85 : colors.Grey10}`,
                  zIndex: 10_000,
                }}
              >
                {tooltipData.tooltip}
              </TooltipInPortal>
            )}
            {eventPicker}
          </div>
        </>
      )}
    </>
  );
};

export default IotTimeline;
